import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"
import { formatBRL } from "../utils/format"

// Subscribes to EventCore::KioskStockChannel and re-pulls the menu after every
// broadcasted refresh ping (a sale/cancel elsewhere touching this kiosk, or an
// item being activated/deactivated), applying stock levels, prices and which
// items are still on offer.
// Price is refreshed here too, not only stock: a POS tab stays open for a whole
// shift, so a price edit or a promotion switched on mid-shift would otherwise
// leave this screen ringing up the price it rendered with while the next device
// to load shows another one.
// The channel only ever sends a tiny {type: "refresh"} signal — no HTML, no item
// data (bare ActionCable for a small ephemeral ping, HTML rendering stays out of
// the channel). The real data comes from the existing GET
// /api/v1/kiosks/:id/menu endpoint the offline PWA client already uses, so
// there's one source of truth for "what's in stock right now".
export default class extends Controller {
  static values = { kioskId: Number, menuUrl: String }

  connect() {
    // Turbo restores a bfcache preview before the real page loads: don't open a
    // socket (or double-subscribe) for a page the browser is about to discard.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this._subscription = consumer.subscriptions.create(
      { channel: "EventCore::KioskStockChannel", kiosk_id: this.kioskIdValue },
      {
        // Catches up immediately on (re)connect — covers the gap between page
        // render and handshake, and any pings missed while offline.
        connected: () => this.#refresh(),
        received: (data) => { if (data.type === "refresh") this.#refresh() }
      }
    )
  }

  disconnect() {
    // Drops this subscription only — the socket itself is shared (see
    // ../channels/consumer.js) and may still be serving other controllers.
    this._subscription?.unsubscribe()
  }

  async #refresh() {
    // The POS tolerates being offline (connectivity-monitor/offline-gate) — a
    // failed fetch here just means stock stays as last known until the next
    // successful refresh, not a page-breaking error.
    try {
      const response = await fetch(this.menuUrlValue, { headers: { Accept: "application/json" } })
      if (!response.ok) return

      const { items } = await response.json()
      this.#applyMenu(items)
    } catch {
      // offline or request failed — nothing to do, next refresh will retry
    }
  }

  #applyMenu(items) {
    items.forEach((item) => {
      const tile = this.element.querySelector(`[data-item-id="${item.id}"]`)
      if (!tile) return

      this.#applyPrice(tile, item)
      this.#applyStockState(tile, item.stock)

      const input = tile.querySelector('[data-kiosk-pos-target="quantityInput"]')
      if (!input) return

      input.dataset.maxQty = item.stock === null ? "" : String(item.stock)

      const current = parseInt(input.value || "0", 10)
      if (item.stock !== null && current > item.stock) input.value = item.stock
    })

    this.#applyAvailability(new Set(items.map((item) => item.id)))

    // kiosk-pos owns the total/balance/cooldown recalculation; this controller
    // only owns menu data, so it hands off via an event instead of reaching
    // into kiosk-pos's private state.
    this.element.dispatchEvent(new CustomEvent("menu:refreshed", { bubbles: true }))
  }

  // Same 0 / 3 thresholds as KioskItemPresenter#stock_state (Ruby side) — no
  // shared build step between Ruby and JS here, so a change to either number
  // has to be mirrored by hand in the other file.
  #applyStockState(tile, stock) {
    const exhausted = stock === 0
    tile.classList.toggle("opacity-40", exhausted)
    tile.classList.toggle("pointer-events-none", exhausted)

    const label = tile.querySelector('[data-role="stock-label"]')
    if (!label) return

    const low = stock !== null && stock <= 3
    label.classList.toggle("text-error", low)
    label.classList.toggle("font-semibold", low)
    label.textContent = exhausted ? "Esgotado" : (stock === null ? "" : `estoque ${stock}`)
  }

  // effective_price is the server's own promotional price — never recomputed
  // here, so what the operator reads is exactly what the card gets charged.
  // data-unit-price feeds kiosk-pos's running total, so both move together.
  #applyPrice(tile, item) {
    const effective = parseFloat(item.effective_price)
    const catalog   = parseFloat(item.price)

    const input = tile.querySelector('[data-kiosk-pos-target="quantityInput"]')
    if (input) input.dataset.unitPrice = String(effective)

    const price = tile.querySelector("[data-pos-price]")
    if (price) price.textContent = formatBRL(effective)

    const original = tile.querySelector("[data-pos-original-price]")
    if (!original) return

    original.textContent = formatBRL(catalog)
    original.hidden      = effective === catalog
  }

  // The menu only lists items that are still sellable, so anything on screen but
  // absent from the payload was deactivated while this page was open: pull the
  // tile and zero whatever was already rung up on it, or the total would include
  // something the server is now going to refuse. Reversible — reactivating puts
  // the tile back on the next ping.
  //
  // display is set inline rather than toggling a `hidden` attribute or class: the
  // tile carries Tailwind's `flex`, whose display would win over both.
  #applyAvailability(offeredIds) {
    this.element.querySelectorAll("[data-item]").forEach((tile) => {
      const withdrawn = !offeredIds.has(Number(tile.dataset.itemId))
      tile.style.display = withdrawn ? "none" : ""
      if (!withdrawn) return

      const input = tile.querySelector("[data-kiosk-pos-target='quantityInput']")
      if (input) input.value = 0
    })
  }
}
